import * as React from "react";

import { useHistoryStorage } from "@/services";
import type { EngagementAccessMeta } from "@/types";

export interface EngagementAccessState {
  loading: boolean;
  access: EngagementAccessMeta | null;
  /** Convenience: can the current user modify this engagement? */
  canEdit: boolean;
  /** Convenience: viewer (visible but not editable). */
  readOnly: boolean;
}

/**
 * SPEC-SHARE-B1 — resolves the current user's access to one engagement.
 * While loading, `readOnly` defaults to TRUE (fail-safe: never expose
 * write controls before we know the role).
 */
export function useEngagementAccess(
  engagementId: string | undefined,
): EngagementAccessState {
  const storage = useHistoryStorage();
  const [loading, setLoading] = React.useState(true);
  const [access, setAccess] = React.useState<EngagementAccessMeta | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!engagementId) {
      setAccess(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    void storage
      .getEngagementAccess(engagementId)
      .then((a) => {
        if (!cancelled) {
          setAccess(a);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error("[useEngagementAccess] failed", err);
        if (!cancelled) {
          setAccess(null);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [engagementId, storage]);

  const canEdit =
    access != null &&
    (access.isAdmin || access.isOwner || access.myRole === "editor");
  // Fail-safe: until loaded, treat as read-only so no write control flashes.
  const readOnly = loading ? true : !canEdit;

  return { loading, access, canEdit, readOnly };
}
